import { localToUtc, zoneAbbreviation } from "./slotTime";
import { getFormattedHours } from "./helper";
import React, { FC, RefObject, useEffect } from "react";
import { IInterviewDate } from "./helper";
import { Form, FormInstance, FormProps, Select } from "antd";
import { axiosInstant, endpoints } from "../../api";
import { IApplicant, IManager } from "../ManagersPanel/helper";
import FormInput from "../FormInput";
import { showError, showSuccess } from "../../Notifications";
import EnrichEditor from "../Editor";
import { emailEditorRules, getSchduleMeetingBody } from "../common/emailBody";
import { MeetingType } from "../common";
import { useUser } from "../../context";

interface Props {
  loading: boolean;
  candidate: IApplicant;
  selectedManager?: IManager;
  interviewDate: IInterviewDate;
  setLoading: (e: boolean) => void;
  formRef: RefObject<FormInstance<any>>;
  setInterviewDate: (e?: IInterviewDate) => void;
  mode?: 'manager' | 'candidate';
  jobApplicationId?: string;
  onScheduled?: () => void;
  selectedUserIds?: string[];
  positionTitle?: string;
  // Set when this form was opened from the recruiter's "Reschedule" action on
  // an already-scheduled interview: posts to the reschedule endpoint, which
  // also cancels the old calendar event before creating the new one.
  isReschedule?: boolean;
}

const ScheduleMeetingForm: FC<Props> = ({
  loading,
  formRef,
  candidate,
  setLoading,
  selectedManager,
  setInterviewDate,
  interviewDate: { date, hour },
  mode = 'manager',
  jobApplicationId,
  onScheduled,
  selectedUserIds,
  positionTitle,
  isReschedule,
}) => {
  const [form] = Form.useForm();
  const closeModal = () => setInterviewDate(undefined);
  const { user } = useUser();

  const handleSubmit: FormProps<{
    title: string;
    emailBody: string;
  }>["onFinish"] = async (values) => {
    if (loading) return;
    if (mode === 'manager' && !selectedManager?._id) closeModal();

    const currentYear = new Date().getFullYear();
    
    // Create a date object for the selected date
    const [month, day] = date.split('-').map(Number);
    const selectedDateTime = new Date(currentYear, month - 1, day, hour);
    const now = new Date();
    
    if (selectedDateTime.getTime() < now.getTime()) {
      showError("Kindly select future dates");
      return;
    }
    
    // The picked slot is in the viewer's zone; the server schedules in UTC.
    const utc = localToUtc(date, hour, currentYear);
    const year = utc.year;

    let payload: any = {
      ...values,
      date: utc.date,
      time: utc.hour,
      year,
    };

    if (mode === 'manager') {
      // Try all possible locations for the candidate ID
      const candidateId =
        candidate._id ||
        candidate.profile?.userId;

      console.log('candidate:', candidate);
      console.log('candidateId:', candidateId);

      if (!candidateId || typeof candidateId !== 'string' || candidateId.length !== 24) {
        showError("No valid candidate ID found!");
        return;
      }

      payload = {
        ...payload,
        candidate: candidateId,
        managerIds: selectedUserIds,
        jobApplicationId,
        date: utc.date,
        time: utc.hour,
        year,
        title: values.title,
      };
      console.log('Scheduling payload:', payload);

      setLoading(true);
      try {
        await axiosInstant.post(
          isReschedule ? endpoints.rescheduleInterview : endpoints.scheduleInterview,
          payload,
        );
        setTimeout(() => {
          closeModal();
          showSuccess(isReschedule ? "Interview rescheduled successfully" : "Meeting reserved successfully");
          setLoading(false);
          if (onScheduled) onScheduled();
        }, 8000);
      } catch (err) {
        showError(err);
        closeModal();
      }
    } else if (mode === 'candidate') {
      const recruiterId = user._id;
      payload = {
        ...payload,
        recruiterId,
        candidateId: candidate._id,
        jobApplicationId,
        date: utc.date,
        time: utc.hour,
        year,
        title: values.title,
      };
      setLoading(true);
      try {
        await axiosInstant.post(
          isReschedule ? endpoints.rescheduleCandidateInterview : '/calendar/scheduleCandidateInterview',
          payload,
        );
        setTimeout(() => {
          closeModal();
          showSuccess(isReschedule ? "Interview rescheduled successfully" : "Meeting reserved successfully");
          setLoading(false);
          if (onScheduled) onScheduled();
        }, 8000);
      } catch (err) {
        showError(err);
        closeModal();
      }
    }
  };

  useEffect(() => {
    if (date && hour && candidate) {
      let interviewerName = "";
      if (mode === 'manager' && selectedManager) {
        interviewerName = selectedManager.name;
      } else if (mode === 'candidate' && user) {
        interviewerName = user.name;
      }
      // Fallback for position
      const position = positionTitle || candidate.position || "the position";
      console.log("Email body position:", position);
      if (interviewerName) {
        const text = getSchduleMeetingBody(
          candidate.name,
          position,
          interviewerName,
          date,
          `${getFormattedHours(hour)} ${zoneAbbreviation()}`,
        );
        form.setFieldValue("emailBody", text);
        // Meeting Type is left unset here on purpose. This used to default to
        // MeetingType.ONLINE, which is literally the Teams option
        // ('Online (via Microsoft teams)') - so a recruiter who forgot to look
        // at the dropdown always got Teams, silently, even when they meant to
        // pick Google Meet. The field is `required`, so the recruiter now has to
        // make an actual choice before the form can submit.
      }
    }
  }, [selectedManager, date, hour, candidate, form, mode, user, positionTitle]);

  return (
    <Form
      onFinish={handleSubmit}
      ref={formRef}
      style={{ marginTop: 20 }}
      form={form}
    >
      <div style={{ marginTop: 20 }}>
        <Form.Item
          label="Meeting Type"
          name="interviewType"
          rules={[
            { required: true, message: `Interview type is required field` },
          ]}
        >
          <Select placeholder="Select meeting type">
            {Object.values(MeetingType).map((type) => (
              <Select.Option key={type} value={type}>
                {type}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <FormInput label="Meeting Title" name="title" isRequired />
        <Form.Item
          label="Email Body"
          name="emailBody"
          rules={emailEditorRules}
          layout="vertical"
          style={{ height: 270 }}
        >
          <EnrichEditor
            placeholder={
              mode === 'candidate'
                ? 'Type email'
                : 'Type email on behalf of Hiring Manager'
            }
          />
        </Form.Item>
      </div>
    </Form>
  );
};

export default ScheduleMeetingForm;
